import React, { useRef, useState } from "react";
import axios from "axios";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@egovernments/digit-ui-react-components";
import { getDocumentByFileStoreId } from "@egovernments/digit-ui-libraries/src/utils/fileType";
import { bindActionCreators } from "redux";
import { formMTDocActions } from "@egovernments/digit-ui-module-core/src/redux/actions";
import { Controller } from "react-hook-form";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import useModal from "@egovernments/digit-ui-libraries/src/hooks/obps/SwalModals/useModal";

export default function FileUploader({
  control,
  name,
  label,
  hasLabel = true,
  isMandatory = false,
  validationMsg,
  required = false,
  tab,
  isNoc = false,
  maxFiles = 1,
  templateMessage = false,
  trigger,
  setFile,
  showModal = false,
  modalMsg = "",
}) {
  const { documents } = useSelector((state) => state.formMT);
  const dispatch = useDispatch();
  const { addFormMTDoc, removeFormMTDoc } = bindActionCreators(formMTDocActions, dispatch);
  const Modal = useModal();
  const [allowFileDialog, setAllowFileDialog] = useState(false);
  const tenantId = Digit.ULBService.getCurrentTenantId();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const fileInputRef = useRef(null);
  const uploadedFiles = documents?.filter((doc) => doc.documentType === name) || [];
  const isAccepted = uploadedFiles?.[0]?.documentStatus === "Accepted";
  const hasUploadedDocument = uploadedFiles.length > 0;
  const canUploadMore = uploadedFiles.length < maxFiles;

  const handleFileUpload = async (file) => {
    // debugger;
    setIsLoading(true);
    setShowToast(null);

    if (!file) {
      setShowToast({ error: true, label: "Please select a file." });
      setIsLoading(false);
      return;
    }
    const invalidMalwareExtensions = [
      "php",
      "php3",
      "php4",
      "php5",
      "phtml",
      "asp",
      "aspx",
      "jsp",
      "jspx",
      "cfm",
      "exe",
      "bat",
      "cmd",
      "sh",
      "com",
      "scr",
      "msi",
      "jar",
      "vbs",
      "ps1",
      "dll",
      "docm",
      "xlsm",
      "pptm",
      "dotm",
      "xltm",
      "html",
      "htm",
      "js",
      "xml",
      "svg",
      "zip",
      "rar",
      "7z",
      "tar",
      "gz",
      "iso",
    ];
    const fileParts = file.name.toLowerCase().split(".");
    const finalExt = fileParts[fileParts.length - 1];
    const intermediateParts = fileParts.slice(0, -1);
    const hasMaliciousExtension = intermediateParts.some((part) => invalidMalwareExtensions.includes(part));
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const fileSize = file.size;

    let allowedFormats = [".pdf"];
    let maxSize = 300 * 1024 * 1024;
    if (hasMaliciousExtension) {
      setShowToast({
        error: true,
        label: "Invalid file name: contains potentially dangerous extensions.",
      });
      setIsLoading(false);
      return;
    }
    if (tab === 1) {
      allowedFormats = [".png", ".jpg", ".jpeg", ".pdf"];
      maxSize = 10 * 1024 * 1024;
    } else if (tab === 2) {
      allowedFormats = [".png", ".jpg", ".jpeg"];
      maxSize = 10 * 1024 * 1024;
    } else if (tab === 3) {
      allowedFormats = [".dwg"];
      maxSize = 300 * 1024 * 1024;
    } else if (tab === 4) {
      allowedFormats = [".dxf"];
      maxSize = 300 * 1024 * 1024;
    }
    else if (tab === "pdf") {
      allowedFormats = [".pdf"];
      maxSize = 30 * 1024 * 1024;
    }

    if (!allowedFormats.includes(`.${fileExtension}`)) {
      setShowToast({ error: true, label: `Allowed formats: ${allowedFormats.join(", ")}` });
      setIsLoading(false);
      return;
    }

    if (fileSize > maxSize) {
      setShowToast({ error: true, label: `File size exceeds limit of ${maxSize / (1024 * 1024)} MB.` });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tenantId", tenantId);
    formData.append("module", "property-upload");
    formData.append("tag", "tag-property");

    try {
      if (!canUploadMore) {
        removeFormMTDoc(name);
      }
      const resp = await axios.post("/filestore/v1/files", formData, {});
      const filesId = resp.data.files[0];

      const uploadedDoc = {
        documentType: name,
        fileStoreId: filesId.fileStoreId,
        isNoc,
        isMandatory,
        additionalDetails: {
          fileName: file.name,
          title: label,
        },
      };

      addFormMTDoc(uploadedDoc);

      if (setFile) {
        setFile(uploadedDoc);
      }

      // addFormMTDoc({
      //   documentType: name,
      //   fileStoreId: filesId.fileStoreId,
      //   isNoc,
      //   isMandatory,
      //   additionalDetails: { fileName: file.name, title: label },
      // });
      if (trigger) {
        trigger(name);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleInputClick = (e) => {
    if (allowFileDialog || !showModal) {
      setAllowFileDialog(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    Modal(
      modalMsg.title,
      modalMsg.body,
      modalMsg.type,
      modalMsg.redirectPath,
      () => {
        setAllowFileDialog(true);
        // ensure state update happens before triggering click
        setTimeout(() => {
          fileInputRef.current?.click();
        }, 0);
      },
      modalMsg.btnText
    );
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div className="inputContainer" style={{ alignItems: "flex-start", display: "block" }}>
        <div style={{ width: "100%", display: "flex" }}>
          {hasLabel && (
            <h3 className="label">
              {label}
              {templateMessage && <span className="template-Text">{"AS PER TEMPLATE"}</span>}
              {isMandatory && <span className="compulsoryField"> *</span>}
            </h3>
          )}

          <Controller
            name={name}
            control={control}
            rules={{
              validate: () => (required ? (uploadedFiles.length > 0 ? true : "Please upload the required file.") : true),
            }}
            render={({ field, fieldState }) => (
              <div className="NewUploader-form-style">
                <Box
                  sx={{
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    p: "0.5rem 0.75rem 0.375rem",
                    borderRadius: "0.25rem",
                    width: "18.25vw",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <div className="text-center">
                    {isLoading ? (
                      <Box
                        sx={{
                          justifyContent: "center",
                          position: "absolute",
                          alignItems: "center",
                          display: "flex",
                          inset: 0,
                        }}
                      >
                        <Loader />
                      </Box>
                    ) : (
                      <React.Fragment>
                        <CloudUploadOutlinedIcon />
                        {/* <span style={{ position: "absolute", fontSize: "0.5rem", marginLeft: "8em" }}>{validationMsg}</span> */}
                      </React.Fragment>
                    )}
                    <input
                      onClick={handleInputClick}
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                    />
                    <small className="error">{validationMsg}</small>
                  </div>
                  {!!showToast?.error && <span className="errorText d-block text-center">{showToast.label}</span>}
                  {fieldState?.error && <span className="errorText d-block text-center">{fieldState.error.message}</span>}
                </Box>
              </div>
            )}
          />
        </div>
        {uploadedFiles.map((doc) => (
          <Box
            key={doc.fileStoreId}
            sx={{
              maxWidth: "100%",
              display: "flex",
              mb: 0.5,
              mt: 1,
            }}
          >
            <Box
              sx={{
                justifyContent: "space-between",
                p: "0.25rem 0 0.25rem 0.75rem",
                backgroundColor: "#eee",
                borderRadius: "0.25rem",
                wordBreak: "break-all",
                fontSize: "0.875rem",
                alignItems: "center",
                display: "flex",
                lineHeight: 1,
                flex: 1,
              }}
            >
              {doc.additionalDetails?.fileName || doc.documentType}
              <Box sx={{ display: "flex" }}>
                <IconButton size="small" color="info" onClick={() => getDocumentByFileStoreId(doc.fileStoreId, setIsPreviewLoading)}>
                  {isPreviewLoading ? <Loader /> : <VisibilityIcon />}
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  // onClick={() => removeFormMTDoc(doc.documentType)}
                  onClick={() => {
                    removeFormMTDoc(doc.documentType);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = null;
                    }
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </div>
    </Box>
  );
}
